import React, { useCallback } from "react";
import { Button } from "@org/ui";
import { Input } from "@org/ui";
import { Label } from "@org/ui";
import { TextArea } from "@org/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@org/ui";
import { FileIcon, EyeIcon } from "lucide-react";
import {
  ContentType,
  ContentItem,
  TextContent,
  ExerciseContent,
  AudioContent,
  DocumentContent,
  MultipleChoiceExerciseContent,
  WrittenExerciseContent,
  RecordedExerciseContent,
  FillInBlankExerciseContent,
  SelectForBlankExerciseContent,
  ConversationalExerciseContent,
  MatchingExerciseContent,
  ScrambleExerciseContent
} from "@org/ui";
import { ExerciseType } from "@org/model";
import { MediaUploader } from "@org/ui";
import { LazyMedia } from "@org/ui";
import { getVideoEmbedInfo } from "@org/ui";
import {
  MultipleChoiceExerciseEditor,
  WrittenExerciseEditor,
  RecordedExerciseEditor,
  FillInBlankExerciseEditor,
  SelectForBlankExerciseEditor,
  ConversationalExerciseEditor,
  MatchingExerciseEditor,
  ScrambleExerciseEditor,
  FileUploadExerciseEditor
} from "../exercise-editors";
import { LessonLinkHint } from "../class/lesson-link-hint";

export function transformToContent(
  data: any,
  type: ContentType,
  contentId: string,
  order: number = 0
): ContentItem {
  return {
    id: contentId,
    type,
    order,
    deleted: false,
    data
  } as ContentItem;
}

const renderBasicFields = (content: ContentItem, updateContent: (content: ContentItem) => void) => (
  <>
    <div>
      <Label htmlFor={`title-${content.id}`}>Title</Label>
      <Input
        id={`title-${content.id}`}
        value={content.data.title || ""}
        onChange={(event) =>
          updateContent({
            ...content,
            data: {
              ...content.data,
              title: event.target.value
            }
          })
        }
        placeholder="Enter title..."
        className="mt-1"
      />
    </div>
    <div>
      <Label htmlFor={`description-${content.id}`}>Description</Label>
      <TextArea
        id={`description-${content.id}`}
        value={content.data.description || ""}
        onChange={(event) =>
          updateContent({
            ...content,
            data: {
              ...content.data,
              description: event.target.value
            }
          })
        }
        placeholder="Enter description..."
        className="mt-1"
      />
      <LessonLinkHint />
    </div>
  </>
);

const renderTextContent = (content: TextContent, updateContent: (content: ContentItem) => void) => (
  <div className="space-y-4">
    {renderBasicFields(content, updateContent)}
    <div>
      <Label htmlFor={`text-${content.id}`}>Text Content</Label>
      <TextArea
        id={`text-${content.id}`}
        value={content.data.text}
        onChange={(event) =>
          updateContent({
            ...content,
            data: {
              ...content.data,
              text: event.target.value
            }
          })
        }
        placeholder="Enter text content..."
        className="min-h-[150px] mt-1"
      />
      <LessonLinkHint />
    </div>
    <div>
      <Label htmlFor={`format-${content.id}`}>Format</Label>
      <Select
        value={content.data.format}
        onValueChange={(value) =>
          updateContent({
            ...content,
            data: {
              ...content.data,
              format: value as TextContent["data"]["format"]
            }
          })
        }
      >
        <SelectTrigger id={`format-${content.id}`} className="w-full mt-1">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="plain">Plain Text</SelectItem>
          <SelectItem value="markdown">Markdown</SelectItem>
          <SelectItem value="html">HTML</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

const renderImageContent = (content: ContentItem, updateContent: (content: ContentItem) => void) => (
  <div className="space-y-4">
    {renderBasicFields(content, updateContent)}
    {content.data.url ? (
      <div className="space-y-2">
        <Label>Image Preview</Label>
        <LazyMedia
          title={content.data.title}
          placeholder={`Image: ${content.data.title || "Untitled image"}`}
          onReplace={() =>
            updateContent({
              ...content,
              data: {
                ...content.data,
                url: ""
              }
            })
          }
        >
          <div className="aspect-video bg-muted rounded-md overflow-hidden">
            <img src={content.data.url} alt={content.data.title || "Image preview"} className="object-contain w-full h-full" />
          </div>
        </LazyMedia>
      </div>
    ) : (
      <div className="space-y-2">
        <Label>Upload Image</Label>
        <MediaUploader
          compact
          acceptedFileTypes={["image/jpeg", "image/png", "image/gif", "image/webp"]}
          title="Upload Image"
          description="Select or drag an image"
          onUploadComplete={(url) =>
            updateContent({
              ...content,
              data: {
                ...content.data,
                url
              }
            })
          }
        />
      </div>
    )}
  </div>
);

const renderVideoContent = (content: ContentItem, updateContent: (content: ContentItem) => void) => {
  const videoUrl = content.data.url || "";
  const embedInfo = getVideoEmbedInfo(videoUrl);

  return (
    <div className="space-y-4">
      {renderBasicFields(content, updateContent)}
      <div className="space-y-2">
        <Label htmlFor={`video-url-${content.id}`}>Embed or external video link</Label>
        <div className="flex gap-2">
          <Input
            id={`video-url-${content.id}`}
            value={videoUrl}
            onChange={(event) =>
              updateContent({
                ...content,
                data: {
                  ...content.data,
                  url: event.target.value
                }
              })
            }
            placeholder="https://www.youtube.com/watch?v=..."
          />
          <Button
            type="button"
            variant="outline"
            disabled={!videoUrl}
            onClick={() =>
              updateContent({
                ...content,
                data: {
                  ...content.data,
                  url: ""
                }
              })
            }
          >
            Clear
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Paste a YouTube, Vimeo, Loom, or other hosted video link. We will store the URL as provided.
        </p>
      </div>
      {videoUrl ? (
        <div className="space-y-2">
          <Label>Video Preview</Label>
          <LazyMedia
            title={content.data.title}
            placeholder={`Video: ${content.data.title || "Untitled video"}`}
            onReplace={() =>
              updateContent({
                ...content,
                data: {
                  ...content.data,
                  url: ""
                }
              })
            }
          >
            <div className="rounded-md overflow-hidden bg-muted aspect-video">
              {embedInfo ? (
                <iframe
                  src={embedInfo.embedUrl}
                  title={content.data.title || "Embedded video"}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video src={videoUrl} controls className="w-full h-full object-cover" />
              )}
            </div>
          </LazyMedia>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Upload Video</Label>
          <MediaUploader
            compact
            acceptedFileTypes={["video/mp4", "video/quicktime", "video/webm"]}
            title="Upload Video"
            description="Select or drag a video"
            onUploadComplete={(url) =>
              updateContent({
                ...content,
                data: {
                  ...content.data,
                  url
                }
              })
            }
          />
        </div>
      )}
    </div>
  );
};

const renderAudioContent = (content: AudioContent, updateContent: (content: ContentItem) => void) => (
  <div className="space-y-4">
    {renderBasicFields(content, updateContent)}
    {content.data.url ? (
      <div className="space-y-2">
        <Label>Audio Preview</Label>
        <LazyMedia
          title={content.data.title}
          placeholder={`Audio: ${content.data.title || "Untitled audio"}`}
          onReplace={() =>
            updateContent({
              ...content,
              data: {
                ...content.data,
                url: ""
              }
            })
          }
        >
          <audio controls className="w-full">
            <source src={content.data.url} />
          </audio>
        </LazyMedia>
      </div>
    ) : (
      <div className="space-y-2">
        <Label>Upload Audio</Label>
        <MediaUploader
          compact
          acceptedFileTypes={["audio/mpeg", "audio/wav", "audio/x-m4a"]}
          title="Upload Audio"
          description="Select or drag an audio file"
          onUploadComplete={(url) =>
            updateContent({
              ...content,
              data: {
                ...content.data,
                url
              }
            })
          }
        />
      </div>
    )}
  </div>
);

const renderDocumentContent = (content: DocumentContent, updateContent: (content: ContentItem) => void) => (
  <div className="space-y-4">
    {renderBasicFields(content, updateContent)}
    <div className="space-y-2">
      <Label htmlFor={`document-url-${content.id}`}>Document URL</Label>
      <div className="flex gap-2">
        <Input
          id={`document-url-${content.id}`}
          value={content.data.url}
          onChange={(event) =>
            updateContent({
              ...content,
              data: {
                ...content.data,
                url: event.target.value
              }
            })
          }
          placeholder="https://..."
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={!content.data.url}
          onClick={() => {
            if (content.data.url && typeof window !== "undefined") {
              window.open(content.data.url, "_blank", "noopener,noreferrer");
            }
          }}
        >
          <EyeIcon className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex gap-2 text-sm text-muted-foreground">
        <FileIcon className="h-4 w-4" />
        <span>Provide a link to a hosted document (PDF, Word, etc.).</span>
      </div>
    </div>
    <div className="space-y-2">
      <Label>Upload Document</Label>
      <MediaUploader
        compact
        acceptedFileTypes={["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"]}
        title="Upload Document"
        description="Select or drag a document"
        onUploadComplete={(url) =>
          updateContent({
            ...content,
            data: {
              ...content.data,
              url
            }
          })
        }
      />
    </div>
  </div>
);

const renderExerciseContent = (content: ExerciseContent, updateContent: (content: ContentItem) => void) => {
  const subtype = content.data.exerciseSubtype;

  switch (subtype) {
    case ExerciseType.MultipleChoice:
      return <MultipleChoiceExerciseEditor content={content as MultipleChoiceExerciseContent} updateContent={updateContent} />;
    case ExerciseType.LongAnswer:
    case ExerciseType.ShortAnswer:
      return <WrittenExerciseEditor content={content as WrittenExerciseContent} updateContent={updateContent} />;
    case ExerciseType.Recorded:
      return <RecordedExerciseEditor content={content as RecordedExerciseContent} updateContent={updateContent} />;
    case ExerciseType.FillInBlank:
      return <FillInBlankExerciseEditor content={content as FillInBlankExerciseContent} updateContent={updateContent} />;
    case ExerciseType.SelectForBlank:
      return <SelectForBlankExerciseEditor content={content as SelectForBlankExerciseContent} updateContent={updateContent} />;
    case ExerciseType.Conversational:
      return <ConversationalExerciseEditor content={content as ConversationalExerciseContent} updateContent={updateContent} />;
    case ExerciseType.Matching:
      return <MatchingExerciseEditor content={content as MatchingExerciseContent} updateContent={updateContent} />;
    case ExerciseType.Scramble:
      return <ScrambleExerciseEditor content={content as ScrambleExerciseContent} updateContent={updateContent} />;
    case ExerciseType.FileUpload:
      return <FileUploadExerciseEditor content={content as ExerciseContent} updateContent={updateContent} />;
    default:
      return <MultipleChoiceExerciseEditor content={content as any} updateContent={updateContent} />;
  }
};

export const useContentRenderer = () => {
  return useCallback((content: ContentItem, updateContent: (content: ContentItem) => void) => {
    switch (content.type) {
      case ContentType.Text:
        return renderTextContent(content as TextContent, updateContent);
      case ContentType.Image:
        return renderImageContent(content, updateContent);
      case ContentType.Video:
        return renderVideoContent(content, updateContent);
      case ContentType.Audio:
        return renderAudioContent(content as AudioContent, updateContent);
      case ContentType.Document:
        return renderDocumentContent(content as DocumentContent, updateContent);
      case ContentType.Exercise:
        return (
          <div className="space-y-4">
            {renderBasicFields(content, updateContent)}
            {renderExerciseContent(content as ExerciseContent, updateContent)}
          </div>
        );
      default:
        return null;
    }
  }, []);
};
